import React from 'react';

import CardActions from '@mui/material/CardActions';

import type { MenuItemProps } from 'material-ui';

import ChatButton from 'src/components/ui/ChatButton';
import type { icons } from 'src/components/ui/Icon';
import ProfilePlusButton from 'src/components/ui/ProfilePlusButton';

import useFollowUnfollow from 'src/hooks/useFollowUnfollow';
import { styledComponent } from 'src/utils/styled';
import FollowUnFollowButton from 'src/components/ui/FollowUnFollowButton';
import AddRemoveContact from 'src/components/ui/AddRemoveContact';
import type { UserProfileQuery } from 'src/types/__generated__/graphql';
import { ContactStatus } from 'src/types/__generated__/graphql';
import type { MenuOption } from 'src/types/MenuOption';
import { getFollowUnfollowMenuOptions } from 'src/utils/popupMenuUtil';

const StyledCardActions = styledComponent(CardActions)<{ $isMobile: boolean }>`
    padding: 0;
    margin-top: 1.5rem;
    margin-left: 0;
    margin-bottom: ${({ $isMobile }) => ($isMobile ? '1rem' : 0)};
    display: flex;
    justify-content: ${({ $isMobile }) => ($isMobile ? 'center' : 0)};
`;

const UserCTAs = ({
    isSelf,
    isMobile,
    userProfile,
}: {
    isSelf: boolean;
    isMobile: boolean;
    userProfile: NonNullable<UserProfileQuery['userProfile']>;
}) => {
    const followUnfollowParams = useFollowUnfollow({
        user: userProfile,
    });

    // no user data available yet
    if (followUnfollowParams.isFollowing === undefined) {
        return null;
    }

    const mobileDotMenuOptions: Record<string, MenuOption> = getFollowUnfollowMenuOptions(
        followUnfollowParams,
        isMobile,
    );

    return (
        <StyledCardActions $isMobile={isMobile}>
            {!isSelf && !isMobile && (
                <FollowUnFollowButton
                    user={userProfile}
                    refetchQueries={['UserProfile']}
                    followButtonProps={{
                        'data-target-id': 'headerSection-follow-button',
                        variant: 'outlined',
                        shrink: true,
                    }}
                />
            )}
            {!isSelf && (
                <AddRemoveContact
                    user={userProfile}
                    contactStatus={userProfile.contactStatusV2}
                    buttonProps={{
                        variant: 'outlined',
                        sx: { height: '2.5rem', pr: '0.75rem' },
                        'data-target-id': 'headerSection-AddContact',
                    }}
                    refetchQueries={['UserProfile']}
                    textOverride={
                        !isMobile
                            ? {
                                  [ContactStatus.Uninvited]: 'profile:addToNetwork',
                                  [ContactStatus.Refused]: 'profile:addToNetwork',
                                  default: 'profile:addToNetwork',
                              }
                            : undefined
                    }
                />
            )}
            {!isSelf && (
                <ChatButton
                    sx={{ height: '2.5rem', pr: '0.75rem' }}
                    userId={userProfile.id}
                    variant="outlined"
                    data-target-id="headerSection-ChatButton"
                />
            )}
            <ProfilePlusButton
                additionalOptions={mobileDotMenuOptions}
                refetchQueries={['UserProfile']}
                user={userProfile}
                buttonProps={{
                    'data-target-id': 'headerSection-plusButton',
                    variant: 'outlined',
                }}
            />
        </StyledCardActions>
    );
};

export default UserCTAs;
